/** Reserved chroma keys for multi-object V2V recolor (one per object slot). */
export const CHROMA_SLOTS = [
  { hex: '#00FF00', label: 'Verde', name: 'green', safe: false },
  { hex: '#FF00FF', label: 'Magenta', name: 'magenta', safe: true },
  { hex: '#00FFFF', label: 'Ciano', name: 'cyan', safe: true },
  { hex: '#0000FF', label: 'Azul', name: 'blue', safe: true },
] as const;

export const MAX_RECOLOR_OBJECTS = CHROMA_SLOTS.length;

export type ChromaSlot = (typeof CHROMA_SLOTS)[number];

export interface RecolorObject {
  id: string;
  name: string;
  slotIndex: number;
  /** Final color chosen by the user (picker). */
  targetHex: string;
  /** Soft alpha mask canvas (same size as freeze frame), painted by user. */
  maskCanvas: HTMLCanvasElement;
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '').trim();
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return (
    '#' +
    [r, g, b]
      .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
      .join('')
  );
}

function clampByte(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

/**
 * Expand + fill small holes in the brush mask so Kling is less likely to skip
 * armrests / gaps between strokes.
 */
export function hardenMaskAlpha(
  maskCanvas: HTMLCanvasElement,
  blurPx = 6,
): HTMLCanvasElement {
  const w = maskCanvas.width;
  const h = maskCanvas.height;
  const soft = document.createElement('canvas');
  soft.width = w;
  soft.height = h;
  const sctx = soft.getContext('2d')!;
  sctx.filter = `blur(${blurPx}px)`;
  sctx.drawImage(maskCanvas, 0, 0);
  sctx.filter = 'none';

  const src = sctx.getImageData(0, 0, w, h);
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const octx = out.getContext('2d')!;
  const img = octx.createImageData(w, h);
  for (let i = 0; i < src.data.length; i += 4) {
    const a = src.data[i + 3] > 12 ? 255 : 0;
    img.data[i] = 255;
    img.data[i + 1] = 255;
    img.data[i + 2] = 255;
    img.data[i + 3] = a;
  }
  octx.putImageData(img, 0, 0);
  return out;
}

/**
 * Export mask for Kling: darkened first-frame context + solid chroma fill on masked pixels.
 * Spatial context helps Kling register the region; hardened alpha fills brush gaps.
 */
export function exportMaskOverlay(
  maskCanvas: HTMLCanvasElement,
  chromaHex: string,
  threshold = 20,
  frameCanvas?: HTMLCanvasElement | null,
): string {
  const hardened = hardenMaskAlpha(maskCanvas, 6);
  const w = hardened.width;
  const h = hardened.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d')!;

  if (frameCanvas && frameCanvas.width === w && frameCanvas.height === h) {
    ctx.drawImage(frameCanvas, 0, 0);
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);
  }

  const src = hardened.getContext('2d')!.getImageData(0, 0, w, h);
  const dst = ctx.getImageData(0, 0, w, h);
  const { r, g, b } = hexToRgb(chromaHex);
  for (let i = 0; i < src.data.length; i += 4) {
    if (src.data[i + 3] > threshold) {
      dst.data[i] = r;
      dst.data[i + 1] = g;
      dst.data[i + 2] = b;
      dst.data[i + 3] = 255;
    }
  }
  ctx.putImageData(dst, 0, 0);
  return out.toDataURL('image/jpeg', 0.92);
}

export interface KeyReplace {
  chromaHex: string;
  targetHex: string;
  /** Max RGB distance (0–441) used as soft edge only; primary match is channel dominance. */
  tolerance?: number;
}

type KeyKind = 'green' | 'magenta' | 'cyan' | 'blue' | 'fallback';

function keyKind(key: { r: number; g: number; b: number }): KeyKind {
  if (key.g >= 180 && key.r < 60 && key.b < 60) return 'green';
  if (key.r >= 180 && key.b >= 180 && key.g < 60) return 'magenta';
  if (key.g >= 180 && key.b >= 180 && key.r < 60) return 'cyan';
  if (key.b >= 180 && key.r < 60 && key.g < 60) return 'blue';
  return 'fallback';
}

/** Hue in degrees 0–360 from RGB. */
function rgbHue(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 1) return 0;
  let h = 0;
  if (max === r) h = ((g - b) / d) % 6;
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return h;
}

/**
 * Soft matte 0–1 for chroma. Looser than before so compressed / dark / spill greens still hit.
 */
export function chromaMatte(
  r: number,
  g: number,
  b: number,
  key: { r: number; g: number; b: number },
): number {
  const kind = keyKind(key);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max === 0 ? 0 : (max - min) / max;
  const hue = rgbHue(r, g, b);

  if (kind === 'green') {
    // Pure / bright green
    const dom = g - Math.max(r, b);
    let m = 0;
    if (g > 45 && dom > 12) {
      m = Math.min(1, Math.max(0, (dom - 8) / 55));
    }
    // Hue window ~70–160° catches yellow-green and blue-green spill from Kling/compression
    if (sat > 0.18 && hue >= 70 && hue <= 160 && g > 40) {
      const hueM = 1 - Math.abs(hue - 120) / 55;
      m = Math.max(m, Math.min(1, hueM * sat * 1.35));
    }
    // Dark green fabric-like leftovers (low lum, still G-dominant)
    if (g > 35 && g >= r + 10 && g >= b + 10 && sat > 0.2) {
      m = Math.max(m, Math.min(1, ((g - Math.max(r, b)) - 6) / 40));
    }
    return Math.min(1, m);
  }

  if (kind === 'magenta') {
    const dom = Math.min(r, b) - g;
    if (r > 40 && b > 40 && dom > 10) return Math.min(1, Math.max(0, (dom - 6) / 55));
    if (sat > 0.2 && (hue >= 280 || hue <= 320) && Math.min(r, b) > 40) {
      return Math.min(1, sat * 1.2);
    }
    return 0;
  }

  if (kind === 'cyan') {
    const dom = Math.min(g, b) - r;
    if (g > 40 && b > 40 && dom > 10) return Math.min(1, Math.max(0, (dom - 6) / 55));
    if (sat > 0.2 && hue >= 160 && hue <= 220) return Math.min(1, sat * 1.2);
    return 0;
  }

  if (kind === 'blue') {
    const dom = b - Math.max(r, g);
    let m = 0;
    if (b > 45 && dom > 12) m = Math.min(1, Math.max(0, (dom - 8) / 55));
    if (sat > 0.18 && hue >= 200 && hue <= 260 && b > 40) {
      m = Math.max(m, Math.min(1, sat * 1.25));
    }
    return Math.min(1, m);
  }

  const dr = r - key.r;
  const dg = g - key.g;
  const db = b - key.b;
  const dist = Math.sqrt(dr * dr + dg * dg + db * db);
  const tol = 160;
  return dist <= tol ? Math.max(0, 1 - dist / tol) : 0;
}

/**
 * Paint target hue onto a textured pixel while keeping luminance and local contrast.
 * Uses multiply-style shading from source luma so fabric folds survive.
 */
export function colorizePreserveTexture(
  sr: number,
  sg: number,
  sb: number,
  tr: number,
  tg: number,
  tb: number,
): { r: number; g: number; b: number } {
  const srcLum = 0.299 * sr + 0.587 * sg + 0.114 * sb;
  const tgtLum = 0.299 * tr + 0.587 * tg + 0.114 * tb || 1;

  // Local contrast vs gray (fabric grain / wrinkles)
  const contrastR = sr - srcLum;
  const contrastG = sg - srcLum;
  const contrastB = sb - srcLum;

  // Scale target to source luminance, then re-apply a fraction of local contrast
  const scale = srcLum / tgtLum;
  const baseR = tr * scale;
  const baseG = tg * scale;
  const baseB = tb * scale;
  const detail = 0.55;

  return {
    r: clampByte(baseR + contrastR * detail),
    g: clampByte(baseG + contrastG * detail),
    b: clampByte(baseB + contrastB * detail),
  };
}

/** Remove residual green/magenta spill on already-recolored pixels (edge cleanup). */
function despillPixel(
  r: number,
  g: number,
  b: number,
  kind: KeyKind,
): { r: number; g: number; b: number } {
  if (kind === 'green' && g > r && g > b) {
    const maxRB = Math.max(r, b);
    const spill = g - maxRB;
    if (spill > 4) {
      return { r, g: clampByte(maxRB + spill * 0.15), b };
    }
  }
  if (kind === 'magenta' && Math.min(r, b) > g + 8) {
    const avg = (r + b) / 2;
    return { r: clampByte(r * 0.85 + avg * 0.15), g, b: clampByte(b * 0.85 + avg * 0.15) };
  }
  return { r, g, b };
}

/**
 * In-place chroma replace.
 * - If `base` is provided (same size): chroma frame = matte, base = texture (original clip).
 * - Else: recolor the chroma pixels themselves (weaker texture — flat Kling fills).
 */
export function recolorImageData(
  chromaFrame: ImageData,
  keys: KeyReplace[],
  base?: ImageData | null,
): void {
  if (!keys.length) return;
  const data = chromaFrame.data;
  const baseData = base && base.width === chromaFrame.width && base.height === chromaFrame.height
    ? base.data
    : null;

  const prepared = keys.map((k) => ({
    c: hexToRgb(k.chromaHex),
    t: hexToRgb(k.targetHex),
    kind: keyKind(hexToRgb(k.chromaHex)),
  }));

  for (let i = 0; i < data.length; i += 4) {
    const cr = data[i];
    const cg = data[i + 1];
    const cb = data[i + 2];

    let bestM = 0;
    let best: (typeof prepared)[0] | null = null;
    for (const k of prepared) {
      const m = chromaMatte(cr, cg, cb, k.c);
      if (m > bestM) {
        bestM = m;
        best = k;
      }
    }
    if (!best || bestM < 0.08) continue;

    // Soft edge: ramp from 0.08 → full by ~0.35
    const alpha = Math.min(1, (bestM - 0.08) / 0.27);

    let sr: number;
    let sg: number;
    let sb: number;
    if (baseData) {
      sr = baseData[i];
      sg = baseData[i + 1];
      sb = baseData[i + 2];
    } else {
      // No separate texture — use chroma pixel (may be near-flat after Kling)
      sr = cr;
      sg = cg;
      sb = cb;
    }

    let painted = colorizePreserveTexture(sr, sg, sb, best.t.r, best.t.g, best.t.b);
    painted = despillPixel(painted.r, painted.g, painted.b, best.kind);

    // When using base texture, output goes onto chroma buffer (we'll draw base first in drawRecolorFrame)
    // Blend toward painted by matte alpha
    const outR = baseData ? sr : cr;
    const outG = baseData ? sg : cg;
    const outB = baseData ? sb : cb;

    data[i] = outR + (painted.r - outR) * alpha;
    data[i + 1] = outG + (painted.g - outG) * alpha;
    data[i + 2] = outB + (painted.b - outB) * alpha;
  }

  // Second pass: despill leftover key hue on near-matte pixels (green streaks)
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    for (const k of prepared) {
      const m = chromaMatte(r, g, b, k.c);
      if (m < 0.12) continue;
      const cleaned = despillPixel(r, g, b, k.kind);
      // Force remaining key toward target if still strongly keyed
      if (m > 0.35) {
        const painted = colorizePreserveTexture(
          baseData ? baseData[i] : cleaned.r,
          baseData ? baseData[i + 1] : cleaned.g,
          baseData ? baseData[i + 2] : cleaned.b,
          k.t.r,
          k.t.g,
          k.t.b,
        );
        const a = Math.min(1, m);
        data[i] = cleaned.r + (painted.r - cleaned.r) * a;
        data[i + 1] = cleaned.g + (painted.g - cleaned.g) * a;
        data[i + 2] = cleaned.b + (painted.b - cleaned.b) * a;
      } else {
        data[i] = cleaned.r;
        data[i + 1] = cleaned.g;
        data[i + 2] = cleaned.b;
      }
      break;
    }
  }
}

let scratchChroma: HTMLCanvasElement | null = null;
let scratchBase: HTMLCanvasElement | null = null;

export function drawRecolorFrame(
  chromaVideo: HTMLVideoElement,
  dest: HTMLCanvasElement,
  keys: KeyReplace[],
  baseVideo?: HTMLVideoElement | null,
): void {
  const w = chromaVideo.videoWidth || dest.width;
  const h = chromaVideo.videoHeight || dest.height;
  if (!w || !h) return;
  if (dest.width !== w || dest.height !== h) {
    dest.width = w;
    dest.height = h;
  }
  const ctx = dest.getContext('2d', { willReadFrequently: true })!;

  if (!scratchChroma) scratchChroma = document.createElement('canvas');
  if (scratchChroma.width !== w || scratchChroma.height !== h) {
    scratchChroma.width = w;
    scratchChroma.height = h;
  }
  const cctx = scratchChroma.getContext('2d', { willReadFrequently: true })!;
  cctx.drawImage(chromaVideo, 0, 0, w, h);
  const chromaData = cctx.getImageData(0, 0, w, h);

  if (baseVideo && baseVideo.readyState >= 2 && baseVideo.videoWidth > 0) {
    if (!scratchBase) scratchBase = document.createElement('canvas');
    if (scratchBase.width !== w || scratchBase.height !== h) {
      scratchBase.width = w;
      scratchBase.height = h;
    }
    const bctx = scratchBase.getContext('2d', { willReadFrequently: true })!;
    bctx.drawImage(baseVideo, 0, 0, w, h);
    const out = bctx.getImageData(0, 0, w, h);
    applyMatteRecolor(out, chromaData, keys);
    ctx.putImageData(out, 0, 0);
    return;
  }

  ctx.drawImage(chromaVideo, 0, 0, w, h);
  if (keys.length) {
    const frame = ctx.getImageData(0, 0, w, h);
    recolorImageData(frame, keys, null);
    ctx.putImageData(frame, 0, 0);
  }
}

/**
 * How much Kling changed this pixel vs the original clip (0–1).
 * Natural greens that Kling left alone stay near 0 → excluded from recolor.
 */
function changeMatte(
  sr: number,
  sg: number,
  sb: number,
  cr: number,
  cg: number,
  cb: number,
): number {
  const dr = sr - cr;
  const dg = sg - cg;
  const db = sb - cb;
  const dist = Math.sqrt(dr * dr + dg * dg + db * db);
  // Compression noise ~10–25; flat chroma fill on furniture usually >> 60
  if (dist < 30) return 0;
  return Math.min(1, (dist - 30) / 75);
}

/**
 * Recolor `out` (base texture) only where Kling changed the frame AND chroma matches a key.
 * Plants / greens that stay identical to the original are skipped even if they look "green".
 */
function applyMatteRecolor(out: ImageData, chroma: ImageData, keys: KeyReplace[]): void {
  if (!keys.length) return;
  const od = out.data;
  const cd = chroma.data;
  const n = od.length / 4;
  const changedBuf = new Float32Array(n);
  const prepared = keys.map((k) => ({
    c: hexToRgb(k.chromaHex),
    t: hexToRgb(k.targetHex),
    kind: keyKind(hexToRgb(k.chromaHex)),
  }));

  for (let p = 0, i = 0; p < n; p++, i += 4) {
    const sr = od[i];
    const sg = od[i + 1];
    const sb = od[i + 2];
    const cr = cd[i];
    const cg = cd[i + 1];
    const cb = cd[i + 2];

    const changed = changeMatte(sr, sg, sb, cr, cg, cb);
    changedBuf[p] = changed;
    if (changed < 0.08) continue;

    let bestM = 0;
    let best: (typeof prepared)[0] | null = null;
    for (const k of prepared) {
      const m = chromaMatte(cr, cg, cb, k.c);
      if (m > bestM) {
        bestM = m;
        best = k;
      }
    }
    if (!best || bestM < 0.08) continue;

    const alpha = Math.min(1, changed * Math.min(1, (bestM - 0.05) / 0.25));
    if (alpha < 0.05) continue;

    let painted = colorizePreserveTexture(sr, sg, sb, best.t.r, best.t.g, best.t.b);
    painted = despillPixel(painted.r, painted.g, painted.b, best.kind);

    od[i] = sr + (painted.r - sr) * alpha;
    od[i + 1] = sg + (painted.g - sg) * alpha;
    od[i + 2] = sb + (painted.b - sb) * alpha;
  }

  // Residual key streaks — only inside Kling change matte
  for (let p = 0, i = 0; p < n; p++, i += 4) {
    if (changedBuf[p] < 0.15) continue;
    const r = od[i];
    const g = od[i + 1];
    const b = od[i + 2];
    const cr = cd[i];
    const cg = cd[i + 1];
    const cb = cd[i + 2];
    for (const k of prepared) {
      const m = Math.max(chromaMatte(cr, cg, cb, k.c), chromaMatte(r, g, b, k.c));
      if (m < 0.2) continue;
      const painted = colorizePreserveTexture(r, g, b, k.t.r, k.t.g, k.t.b);
      const cleaned = despillPixel(painted.r, painted.g, painted.b, k.kind);
      const a = Math.min(1, m * changedBuf[p]);
      od[i] = r + (cleaned.r - r) * a;
      od[i + 1] = g + (cleaned.g - g) * a;
      od[i + 2] = b + (cleaned.b - b) * a;
      break;
    }
  }
}

function waitEvent(target: EventTarget, event: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const ok = () => {
      cleanup();
      resolve();
    };
    const fail = () => {
      cleanup();
      reject(new Error(`Evento ${event} falhou`));
    };
    const cleanup = () => {
      target.removeEventListener(event, ok);
      target.removeEventListener('error', fail);
    };
    target.addEventListener(event, ok, { once: true });
    target.addEventListener('error', fail, { once: true });
  });
}

/**
 * Fetch remote video into a same-origin blob URL so canvas getImageData is not CORS-tainted.
 * Passes through blob:/data: URLs unchanged.
 */
export async function toSameOriginVideoUrl(url: string): Promise<{ objectUrl: string; revoke: () => void }> {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) {
    return { objectUrl: url, revoke: () => {} };
  }
  const cacheBusted = url + (url.includes('?') ? '&' : '?') + 'cb=' + Date.now();
  const resp = await fetch(cacheBusted, { mode: 'cors', credentials: 'omit' });
  if (!resp.ok) throw new Error(`Falha ao baixar vídeo chroma (HTTP ${resp.status}).`);
  const blob = await resp.blob();
  if (!blob.size) throw new Error('Vídeo chroma vazio.');
  const objectUrl = URL.createObjectURL(blob);
  return {
    objectUrl,
    revoke: () => URL.revokeObjectURL(objectUrl),
  };
}

/**
 * Bake chroma video → recolored WebM.
 * Pass `baseVideoUrl` (original clip) to keep fabric texture; chroma is used as matte only.
 */
export async function bakeRecolorVideo(
  chromaVideoUrl: string,
  keys: KeyReplace[],
  options?: {
    fps?: number;
    keepAudio?: boolean;
    baseVideoUrl?: string | null;
    onProgress?: (pct: number) => void;
  },
): Promise<Blob> {
  const fps = options?.fps ?? 24;
  const local = await toSameOriginVideoUrl(chromaVideoUrl);
  const baseLocal = options?.baseVideoUrl
    ? await toSameOriginVideoUrl(options.baseVideoUrl)
    : null;
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.src = local.objectUrl;
    await waitEvent(video, 'loadedmetadata');
    if (video.readyState < 2) {
      video.load();
      await waitEvent(video, 'loadeddata');
    }

    let baseVideo: HTMLVideoElement | null = null;
    if (baseLocal) {
      baseVideo = document.createElement('video');
      baseVideo.muted = true;
      baseVideo.playsInline = true;
      baseVideo.preload = 'auto';
      baseVideo.src = baseLocal.objectUrl;
      await waitEvent(baseVideo, 'loadedmetadata');
      if (baseVideo.readyState < 2) {
        baseVideo.load();
        await waitEvent(baseVideo, 'loadeddata');
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const duration = Math.max(0.1, video.duration || 1);

    const stream = canvas.captureStream(fps);
    if (options?.keepAudio !== false) {
      try {
        const audioVideo = document.createElement('video');
        audioVideo.src = local.objectUrl;
        audioVideo.muted = false;
        await waitEvent(audioVideo, 'loadedmetadata');
        const anyVid = audioVideo as HTMLVideoElement & { captureStream?: () => MediaStream };
        if (typeof anyVid.captureStream === 'function') {
          await audioVideo.play().catch(() => {});
          const aStream = anyVid.captureStream();
          aStream.getAudioTracks().forEach((t) => stream.addTrack(t));
          audioVideo.pause();
          audioVideo.currentTime = 0;
        }
      } catch {
        /* no audio track — ok */
      }
    }

    const mime =
      MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm;codecs=vp8')
          ? 'video/webm;codecs=vp8'
          : 'video/webm';

    const chunks: Blob[] = [];
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 6_000_000 });
    recorder.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };

    const stopped = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });

    recorder.start(200);
    const frameDt = 1 / fps;
    for (let t = 0; t <= duration + 0.001; t += frameDt) {
      const ct = Math.min(t, duration);
      video.currentTime = ct;
      await waitEvent(video, 'seeked');
      if (baseVideo) {
        const baseDur = baseVideo.duration || duration;
        baseVideo.currentTime = Math.min(ct, baseDur);
        await waitEvent(baseVideo, 'seeked');
      }
      drawRecolorFrame(video, canvas, keys, baseVideo);
      options?.onProgress?.(Math.min(99, Math.round((t / duration) * 100)));
      await new Promise((r) => setTimeout(r, 1000 / fps / 2));
    }
    recorder.stop();
    await stopped;
    stream.getTracks().forEach((t) => t.stop());
    options?.onProgress?.(100);

    return new Blob(chunks, { type: 'video/webm' });
  } finally {
    local.revoke();
    baseLocal?.revoke();
  }
}

export function defaultTargetForSlot(slotIndex: number): string {
  // Distinct preview defaults (not chroma) so pickers start usable
  const defaults = ['#C9A227', '#5B8C5A', '#D4A574', '#6B7280'];
  return defaults[slotIndex % defaults.length];
}

export { rgbToHex };
